import React from "react";
import { Link, useParams } from "react-router-dom";

import "./Article.css";
import Header from "../Shared/Header/Header";
import SubHeader from "../Shared/SubHeader/SubHeader";
import Footer from "../Shared/Footer/Footer";
import { BsFillArrowRightCircleFill } from "react-icons/bs";

const Article = () => {
  const { slug } = useParams();

  const article = (() => {
    switch (slug) {
      // 1. ARTYKUŁ: "ADHD u dorosłych – co to jest i jak rozpoznać?"
      case "adhd-u-doroslych-od-objawow-do-diagnozy":
      case "adhd-u-doroslych-co-to-jest-i-jak-rozpoznac":
        return {
          title: "ADHD u dorosłych – co to jest i jak rozpoznać?",
          dateISO: "2025-11-27",
          dateLabel: "27 listopad 2025",
          subtitle:
            "Czym jest ADHD u dorosłych, jakie daje objawy i kiedy warto zgłosić się po profesjonalną diagnozę?",
          content: (
            <>
              <p>
                Masz wrażenie, że Twój umysł ciągle „przeskakuje” między
                różnymi myślami, a codzienne obowiązki wymykają się spod
                kontroli? Zdarza Ci się zapominać o ważnych spotkaniach, gubić
                klucze lub nieustannie odkładać zadania na później, mimo
                szczerych chęci?
              </p>
              <p>
                W dorosłym życiu takie trudności często budzą frustrację i
                prowadzą do pytań – czy to po prostu roztargnienie i stres, czy
                może coś więcej? Coraz więcej dorosłych odkrywa, że przyczyną
                ich wieloletnich problemów z koncentracją i organizacją może
                być ADHD.
              </p>

              <p>
                ADHD najczęściej kojarzone jest z energicznym dzieckiem, które
                nie potrafi usiedzieć w szkolnej ławce. Tymczasem to zaburzenie
                dotyczy również osób dorosłych – i bywa, że przez wiele lat
                pozostaje niewykryte. Szacuje się, że ADHD występuje u ok. 3–5%
                populacji dorosłych. Wiele osób dowiaduje się o nim dopiero po
                latach zmagania się z przypisywanymi sobie „słabościami”
                charakteru.
              </p>

              <p>
                Celem tego artykułu jest wyjaśnienie, czym właściwie jest ADHD u
                dorosłych, jak objawia się na co dzień i jak odróżnić je od
                innych problemów. Chcemy też zachęcić Cię do szukania
                profesjonalnej pomocy, jeśli rozpoznajesz u siebie opisane
                trudności – zrozumienie ich natury to pierwszy krok do realnej
                zmiany.
              </p>

              <h2>Definicja i kluczowe pojęcia ADHD</h2>

              <p>
                ADHD (ang. Attention Deficit Hyperactivity Disorder – zespół
                nadpobudliwości psychoruchowej z deficytem uwagi) zaliczane
                jest do zaburzeń neurorozwojowych. Oznacza to, że jego początki
                sięgają wczesnego rozwoju układu nerwowego, zazwyczaj
                dzieciństwa.
              </p>

              <p>
                Klasycznie wyróżnia się trzy główne grupy objawów: nieuwagę,
                nadmierną aktywność (nadpobudliwość) oraz impulsywność. Osoba z
                ADHD może mieć trwałe trudności z utrzymaniem koncentracji,
                doświadczać poczucia wewnętrznego „niespokojnego silnika” i
                częściej działać pod wpływem impulsu, bez zastanowienia nad
                konsekwencjami.
              </p>

              <p>
                Ważne jest to, że objawy ADHD pojawiają się wcześnie – według
                kryteriów diagnostycznych muszą być obecne już w dzieciństwie,
                choć u dorosłych przyjmują nieco inną formę niż u dzieci.
                Zazwyczaj dorosłe ADHD jest więc kontynuacją zaburzenia z
                dzieciństwa, a nie czymś, co „nagle się pojawia”.
              </p>

              <p>
                Część osób doświadcza z wiekiem pewnej poprawy – szczególnie w
                zakresie widocznej nadruchliwości. Mimo to u większości
                trudności nie znikają całkowicie. Po prostu życie stawia coraz
                większe wymagania (studia, praca, dom, rodzina), przez co
                problemy z organizacją czy uwagą zaczynają boleśniej „ciążyć”.
              </p>

              <p>
                ADHD nie jest wynikiem złego wychowania, lenistwa czy braku
                inteligencji. To realne zaburzenie o podłożu neurobiologicznym.
                Osoby z ADHD często latami słyszą komunikaty w stylu „weź się w
                garść” albo „gdybyś bardziej się postarał_a…”. Tymczasem badania
                pokazują jasno: mózg osoby z ADHD działa inaczej – i nie jest to
                kwestia „dobrej woli”.
              </p>

              <h2>Objawy ADHD u dorosłych w codziennym życiu</h2>

              <p>
                Objawy ADHD u dorosłych potrafią być subtelniejsze niż u dzieci,
                przez co łatwo je zrzucić na „charakter” albo stresujący styl
                życia. Poniżej kilka typowych trudności, które często zgłaszają
                dorośli z ADHD:
              </p>

              <ul>
                <li>
                  <strong>Problemy z koncentracją uwagi:</strong> myśli
                  „odpływają” w pracy, przy czytaniu czy rozmowie; łatwo
                  zaczynasz wiele rzeczy naraz i nie kończysz ich; dłuższe
                  projekty ciągną się aż do ostatniej chwili przed deadlinem.
                </li>
                <li>
                  <strong>Wewnętrzny niepokój i nadmierna aktywność:</strong>{" "}
                  nie musisz już „biegać po klasie”, ale możesz mieć trudność,
                  by spokojnie usiedzieć, zrelaksować się, obejrzeć film do
                  końca. Pojawia się wiercenie się, stukanie nogą, potrzeba
                  ciągłego „robienia czegoś”.
                </li>
                <li>
                  <strong>Impulsywność:</strong> podejmowanie decyzji pod
                  wpływem chwili – wydatki, zmiany pracy, ostre komentarze w
                  emocjach – a dopiero później refleksja nad skutkami.
                </li>
                <li>
                  <strong>Dezorganizacja i prokrastynacja:</strong> trudności z
                  planowaniem dnia, odkładanie ważnych spraw, bałagan w
                  dokumentach i mailach, spóźnienia czy zaległe rachunki.
                </li>
                <li>
                  <strong>Zapominanie i „głupie błędy”:</strong> gubienie
                  kluczy, niedopatrzenia w pracy, zapominanie o terminach – nawet
                  jeśli bardzo chcesz „w końcu to ogarnąć”.
                </li>
                <li>
                  <strong>Trudności w relacjach:</strong> przerywanie w
                  rozmowie, nieoddzwanianie, zapominanie o ustaleniach, szybkie
                  nudzenie się rutyną – to wszystko może wpływać na związki i
                  kontakty z innymi.
                </li>
              </ul>

              <p>
                U jednej osoby na pierwszy plan wysunie się roztargnienie i
                zapominalstwo, u innej – emocjonalne wybuchy i impulsywność, a
                u kolejnej – mieszanka wszystkiego naraz. Kluczowe jest to, że
                objawy realnie utrudniają codzienne funkcjonowanie, wpływają na
                pracę, relacje, finanse i poczucie własnej wartości.
              </p>

              <h2>ADHD a inne trudności – jak odróżnić?</h2>

              <p>
                Kłopoty z koncentracją, zmęczenie czy rozdrażnienie mogą
                towarzyszyć także innym problemom psychicznym – depresji,
                zaburzeniom lękowym czy wypaleniu zawodowemu. Nic dziwnego, że
                wiele osób ma wątpliwość: „Czy to ADHD, depresja, czy po prostu
                za dużo stresu?”.
              </p>

              <p>
                W depresji na pierwszy plan wysuwa się obniżony nastrój,
                poczucie beznadziei i utrata energii. Problemy z koncentracją
                pojawiają się wtedy jako skutek ogólnego „załamania”
                psychicznego. W ADHD trudności z uwagą są obecne od dzieciństwa
                i mają raczej stały charakter – choć ich nasilenie może się
                zmieniać.
              </p>

              <p>
                W zaburzeniach lękowych problemy z koncentracją wynikają z
                ciągłego napięcia i zamartwiania się („ciągle analizuję
                najgorsze scenariusze”), podczas gdy w ADHD uwaga „ucieka” głównie
                przez rozproszenie bodźcami i nudę. Jednocześnie te zaburzenia
                często współwystępują, co dodatkowo komplikuje obraz.
              </p>

              <p>
                Wypalenie zawodowe to z kolei reakcja na długotrwały stres w
                pracy – gdy zmienisz środowisko, zadbasz o odpoczynek, objawy
                stopniowo słabną. W ADHD chaos i trudności z organizacją są
                obecne niezależnie od miejsca pracy czy konkretnego projektu.
              </p>

              <p>
                Dlatego tak ważna jest profesjonalna diagnoza – specjalista
                (psychiatra lub psycholog) może zebrać szczegółowy wywiad,
                uwzględnić historię z dzieciństwa, współwystępowanie innych
                trudności i dopiero na tej podstawie postawić rozpoznanie.
              </p>

              <h2>Co mówi nauka o ADHD u dorosłych?</h2>

              <p>
                Jeszcze niedawno ADHD u dorosłych było dla wielu kontrowersyjne
                („przecież to choroba dzieci”). Dziś wiemy z badań, że ADHD
                może utrzymywać się przez całe życie. Badania neuroobrazowe
                pokazują różnice w funkcjonowaniu obszarów mózgu
                odpowiedzialnych m.in. za uwagę i kontrolę impulsów, a badania
                genetyczne wskazują na wysoki stopień dziedziczności.
              </p>

              <p>
                Dobrze udokumentowano również skuteczność leczenia – zarówno
                farmakoterapii, jak i odpowiednio ukierunkowanej psychoterapii.
                Osoby z prawidłowo rozpoznanym i leczonym ADHD lepiej
                funkcjonują zawodowo, rzadziej doświadczają poważnych kryzysów
                życiowych i rzadziej „gaszą pożary” w pracy i relacjach.
              </p>

              <p>
                Warto też podkreślić różnice między płciami – kobiety częściej
                mają tzw. „cichy” podtyp z przewagą nieuwagi, bez wyraźnej
                nadruchliwości. Przez to ich trudności bywają niedostrzegane, a
                diagnoza pojawia się później, często dopiero po wielu latach
                zmagań z poczuciem „bycia nie dość dobrą”.
              </p>

              <h2>Konsekwencje ADHD w pracy, relacjach i zdrowiu</h2>

              <p>Nieleczone ADHD może wpływać na wszystkie ważne obszary życia:</p>

              <ul>
                <li>
                  <strong>Praca:</strong> trudności z terminami, bałagan w
                  zadaniach, częste zmiany pracy, poczucie niewykorzystywania
                  własnego potencjału.
                </li>
                <li>
                  <strong>Relacje:</strong> konflikty z powodu zapominalstwa,
                  impulsywnych reakcji czy „znikania” myślami w rozmowie.
                </li>
                <li>
                  <strong>Finanse i organizacja:</strong> problemy z pilnowaniem
                  rachunków, impulsywne zakupy, życie „od kryzysu do kryzysu”.
                </li>
                <li>
                  <strong>Zdrowie:</strong> większe ryzyko wypadków,
                  nieregularny sen, gorsza dieta, częstsze współwystępowanie
                  depresji, lęku czy uzależnień.
                </li>
              </ul>

              <p>
                Jednocześnie osoby z ADHD często są bardzo kreatywne, szybkie w
                myśleniu i pełne energii – pod warunkiem, że nauczą się
                zarządzać swoimi trudnościami tak, by mocne strony mogły
                „przebić się” na pierwszy plan.
              </p>

              <h2>Jak można sobie pomóc?</h2>

              <p>
                ADHD jest zaburzeniem przewlekłym, ale nie oznacza „wyroku”.
                Dostępnych jest wiele form wsparcia:
              </p>

              <ul>
                <li>
                  <strong>Psychoedukacja:</strong> zrozumienie, czym jest ADHD i
                  jak działa Twój mózg, często przynosi ogromną ulgę – to nie
                  lenistwo, tylko inny sposób funkcjonowania.
                </li>
                <li>
                  <strong>Farmakoterapia:</strong> leki (najczęściej
                  stymulujące) zwiększają dostępność dopaminy i noradrenaliny,
                  poprawiając koncentrację, planowanie i hamowanie impulsów.
                  Dobiera je lekarz psychiatra.
                </li>
                <li>
                  <strong>Psychoterapia/CBT i coaching ADHD:</strong> pomagają
                  wypracować konkretne strategie – planowanie dnia, praca z
                  prokrastynacją, regulacja emocji, budowanie realistycznej
                  samooceny.
                </li>
                <li>
                  <strong>Strategie dnia codziennego:</strong> kalendarz,
                  przypomnienia, stałe miejsca na ważne rzeczy, podział zadań na
                  małe kroki, metoda Pomodoro, ograniczanie rozpraszaczy.
                </li>
                <li>
                  <strong>Wsparcie bliskich i grupy wsparcia:</strong> rozmowa z
                  rodziną, partnerem, innymi osobami z ADHD – po to, by nie
                  zostać z tym wszystkim samemu.
                </li>
              </ul>

              <h2>Kiedy warto zgłosić się po pomoc?</h2>

              <p>
                Warto rozważyć konsultację u specjalisty, jeśli widzisz, że
                opisane objawy realnie dezorganizują Twoje życie – np. tracisz
                pracę przez roztargnienie, masz poważne kłopoty finansowe,
                narastające konflikty w relacji albo coraz częściej towarzyszy
                Ci poczucie bezradności i beznadziei.
              </p>

              <p>
                Samotne mierzenie się z tym wszystkim kosztuje bardzo dużo
                energii. Profesjonalna diagnoza ADHD nie jest „łatką”, ale
                narzędziem do lepszego zrozumienia siebie i punktem startu do
                skutecznej pomocy – czy będzie nią terapia, zmiana stylu życia,
                farmakoterapia, czy połączenie tych dróg.
              </p>

              <p>
                Jeśli czujesz, że to, co czytasz, brzmi jak opis Twojej
                codzienności – to może być dobry moment, by zrobić kolejny krok
                i porozmawiać o tym ze specjalistą.
              </p>
            </>
          ),
        };

      // 2. ARTYKUŁ: "Objawy ADHD u dorosłych – jak rozpoznać w codziennym życiu?"
      case "objawy-adhd-u-doroslych-w-codziennym-zyciu":
        return {
          title: "Objawy ADHD u dorosłych – jak rozpoznać w codziennym życiu?",
          dateISO: "2025-11-23",
          dateLabel: "23 listopad 2025",
          subtitle:
            "Roztrzepanie, zapominalstwo, chaos – jak odróżnić ADHD od zwykłego braku organizacji?",
          content: (
            <>
              <p>
                Poranny pośpiech: wstajesz spóźniony_a, bo kolejny raz
                „przedrzemałeś_aś” trzy budziki. W biegu szukasz kluczy, które
                znów gdzieś zniknęły. W pracy jedno zadanie jest rozgrzebane,
                drugie dopiero zaczęte, a myśli już biegną do kolejnego pomysłu.
                Wieczorem wracasz zmęczony_a, chociaż masz poczucie, że tak
                naprawdę niewiele udało się dokończyć. Brzmi znajomo?
              </p>
              <p>
                Tak wygląda codzienność wielu dorosłych z ADHD. W tym tekście
                przyglądamy się temu, jak objawy ADHD przenikają zwykłe
                sytuacje z życia i co może pomóc ten chaos lepiej ogarnąć.
              </p>

              <h2>Czym jest ADHD u dorosłych – krótkie przypomnienie</h2>

              <p>
                ADHD to zaburzenie neurorozwojowe. Nie „pojawia się” nagle w
                wieku 30 czy 40 lat, tylko towarzyszy człowiekowi od
                dzieciństwa. U dorosłych wygląda jednak często inaczej niż u
                dzieci – mniej widać typową nadruchliwość, a bardziej:
              </p>

              <ul>
                <li>problemy z uwagą i koncentracją,</li>
                <li>trudności w planowaniu i domykaniu spraw,</li>
                <li>łatwe rozpraszanie się bodźcami,</li>
                <li>impulsywne decyzje i reakcje emocjonalne.</li>
              </ul>

              <p>
                Na zewnątrz może to wyglądać jak „roztrzepanie”, „bałaganiarstwo”
                albo „brak silnej woli”, ale w praktyce wynika z innego
                funkcjonowania mózgu – szczególnie obszarów odpowiedzialnych za
                tzw. funkcje wykonawcze (planowanie, hamowanie impulsów,
                podtrzymywanie uwagi).
              </p>

              <h2>Objawy ADHD w pracy</h2>

              <p>
                Praca zawodowa jest jednym z miejsc, gdzie ADHD szybko daje o
                sobie znać. Typowe trudności to:
              </p>

              <ul>
                <li>
                  <strong>Rozproszenie i skakanie między zadaniami:</strong>{" "}
                  zaczynasz raport, po chwili jesteś już w mailach, potem coś
                  sprawdzasz w internecie – i nagle mija godzina.
                </li>
                <li>
                  <strong>Prokrastynacja:</strong> im większy i bardziej
                  złożony projekt, tym większa pokusa, żeby go „na chwilę”
                  odłożyć. Realnie rusza dopiero, kiedy deadline jest już bardzo
                  blisko.
                </li>
                <li>
                  <strong>Trudność z długimi, nudnymi zadaniami:</strong> mózg
                  z ADHD „odjeżdża”, kiedy bodźce są mało ciekawe. Siedzenie nad
                  tabelkami, procedurami czy dokumentacją bywa szczególnie
                  męczące.
                </li>
                <li>
                  <strong>Spóźnienia, zgubione informacje, pomyłki:</strong>{" "}
                  niechcący przeoczone maile, niezapisane ustalenia, brak
                  notatki ze spotkania, przez co coś ważnego wypada z głowy.
                </li>
              </ul>

              <p>
                Na dłuższą metę może to prowadzić do poczucia, że „ciągle jestem
                krok za innymi”, a w konsekwencji do spadku samooceny i
                wypalenia.
              </p>

              <h2>Objawy ADHD w domu i organizacji codzienności</h2>

              <p>
                ADHD nie „wyłącza się” po pracy – jego objawy widać też w domu:
              </p>

              <ul>
                <li>
                  wieczny bałagan w papierach, szufladach, mailach, aplikacjach,
                </li>
                <li>
                  odkładanie domowych spraw („zadzwonię jutro…”, „posprzątam w
                  weekend…”),
                </li>
                <li>
                  gubienie rzeczy – kluczy, dokumentów, ładowarek, portfela,
                </li>
                <li>
                  trudności z utrzymaniem stałych nawyków (np. regularne
                  sprzątanie, pranie, płacenie rachunków na czas).
                </li>
              </ul>

              <p>
                Z zewnątrz może wyglądać to jak lenistwo, ale osoby z ADHD
                często inwestują ogrom energii, żeby „jakoś to ogarnąć”. Tyle
                że systemy, które świetnie działają u innych, u nich często się
                po prostu nie sprawdzają.
              </p>

              <h2>Objawy ADHD w relacjach</h2>

              <p>
                ADHD wpływa również na związki, przyjaźnie i kontakty rodzinne.
                Typowe sytuacje:
              </p>

              <ul>
                <li>
                  <strong>Zapominanie o ustaleniach:</strong> obiecujesz, że
                  coś załatwisz, ale wypada Ci to z głowy. Dla Ciebie to kolejna
                  „wtopa”, dla bliskich – sygnał, że są mało ważni.
                </li>
                <li>
                  <strong>Impulsywne reakcje:</strong> szybkie, ostre słowa
                  wypowiedziane w emocjach; dopiero później przychodzi refleksja
                  i poczucie winy.
                </li>
                <li>
                  <strong>„Znikanie” w trakcie rozmowy:</strong> druga osoba
                  mówi, a Twój umysł nagle odpływa. Partner/partnerka może to
                  odbierać jako brak zainteresowania.
                </li>
                <li>
                  <strong>Nuda w rutynie:</strong> powtarzalność i brak
                  nowości męczą – pojawia się potrzeba ciągłych zmian, bodźców,
                  impulsów.
                </li>
              </ul>

              <p>
                Kiedy nikt nie wie o ADHD, łatwo interpretować takie zachowania
                jako „brak zaangażowania” czy „egoizm”. Diagnoza często pomaga
                uporządkować te sytuacje i zacząć szukać rozwiązań, zamiast
                szukać winnego.
              </p>

              <h2>Jak odróżnić ADHD od zwykłego braku organizacji?</h2>

              <p>
                Każdemu zdarza się zapomnieć o rachunku, zawalić termin czy
                przeoczyć maila. W ADHD różnica polega na skali i stałości
                problemu:
              </p>

              <ul>
                <li>
                  trudności trwają od dzieciństwa (nawet jeśli kiedyś były mniej
                  widoczne),
                </li>
                <li>
                  pojawiają się w różnych obszarach życia – praca, dom, relacje,
                  nauka,
                </li>
                <li>
                  mimo starań, notatek, aplikacji, postanowień „od jutra będzie
                  inaczej” – wzorce wciąż wracają,
                </li>
                <li>
                  konsekwencje są realne: utrata pracy, konflikty, długi,
                  narastające poczucie porażki.
                </li>
              </ul>

              <p>
                Jeśli widzisz, że to nie jest tylko kwestia „gorszego okresu”
                czy chwilowego przemęczenia, tylko coś, co ciągnie się latami –
                warto przyjrzeć się tematowi poważniej.
              </p>

              <h2>Praktyczne strategie na codzienny chaos</h2>

              <p>Co możesz zrobić samodzielnie, zanim trafisz do gabinetu?</p>

              <ul>
                <li>
                  <strong>Małe, powtarzalne rutyny:</strong> jedno miejsce na
                  klucze, portfel i dokumenty; stała pora na sprawdzanie
                  kalendarza na kolejny dzień.
                </li>
                <li>
                  <strong>Korzystanie z technologii:</strong> alarmy, przypomnienia,
                  kalendarz z powiadomieniami. Smartfon jako „zewnętrzna
                  pamięć”.
                </li>
                <li>
                  <strong>Podział zadań na małe kroki:</strong> zamiast „napisać
                  raport”, wpisz w listę: „otworzyć plik”, „wypisać nagłówki”,
                  „uzupełnić sekcję 1” itd.
                </li>
                <li>
                  <strong>Praca blokami czasowymi:</strong> np. metoda Pomodoro:
                  25 minut pracy, 5 minut przerwy – timer bardzo pomaga utrzymać
                  fokus.
                </li>
                <li>
                  <strong>Porządkowanie otoczenia:</strong> im mniej bodźców na
                  biurku i w pokoju, tym łatwiej skupić się na konkretnej
                  rzeczy.
                </li>
              </ul>

              <h2>Kiedy zgłosić się do specjalisty?</h2>

              <p>Warto rozważyć konsultację, jeśli:</p>

              <ul>
                <li>
                  mimo starań chaos wciąż wraca i zaczyna mieć poważne
                  konsekwencje,
                </li>
                <li>
                  praca, studia czy prowadzenie domu stają się ciągłą walką
                  „o przetrwanie”,
                </li>
                <li>
                  relacje z bliskimi psują się przez zapominalstwo, spóźnienia,
                  impulsywne reakcje,
                </li>
                <li>
                  coraz częściej towarzyszy Ci poczucie bezradności, wstydu,
                  myśli „ze mną jest coś nie tak”.
                </li>
              </ul>

              <p>
                Psycholog lub psychiatra specjalizujący się w ADHD może pomóc
                odróżnić ADHD od innych trudności (np. depresji, zaburzeń
                lękowych, wypalenia) i zaproponować plan działania – od
                psychoedukacji i terapii, po ewentualne leczenie
                farmakologiczne.
              </p>

              <p>
                Jeśli w opisach odnajdujesz siebie, to może być dobry moment,
                żeby przyjrzeć się temu bliżej i poszukać wsparcia – także w
                formie diagnozy online.
              </p>
            </>
          ),
        };

      // 3. ARTYKUŁ: "ADHD czy coś innego? Różnicowanie z depresją, lękiem i wypaleniem"
      case "adhd-czy-cos-innego-roznicowanie-z-depresja-lekiem-i-wypaleniem":
        return {
          title:
            "ADHD czy coś innego? Różnicowanie z depresją, lękiem i wypaleniem",
          dateISO: "2025-11-18",
          dateLabel: "18 listopad 2025",
          subtitle:
            "Kiedy problemy z koncentracją to ADHD, a kiedy efekt depresji, lęku albo wypalenia?",
          content: (
            <>
              <p>
                Masz problemy z koncentracją, czujesz zmęczenie i rozdrażnienie.
                Zastanawiasz się: „Czy to depresja? Wypalenie? A może ADHD?”.
                Objawy różnych trudności psychicznych często się na siebie
                nakładają, przez co łatwo się pogubić.
              </p>
              <p>
                W tym artykule przyglądamy się temu, jak odróżnić ADHD od
                depresji, zaburzeń lękowych i wypalenia, oraz dlaczego dobra
                diagnoza ma kluczowe znaczenie dla leczenia.
              </p>

              <h2>ADHD w pigułce – co je wyróżnia?</h2>

              <p>
                ADHD (zespół nadpobudliwości psychoruchowej z deficytem uwagi)
                zaliczane jest do zaburzeń neurorozwojowych. To oznacza, że:
              </p>

              <ul>
                <li>początki sięgają dzieciństwa,</li>
                <li>
                  objawy są obecne długo – zwykle przez całe życie (choć ich
                  nasilenie się zmienia),
                </li>
                <li>
                  trudności dotyczą głównie uwagi, impulsywności i organizacji.
                </li>
              </ul>

              <p>
                U dorosłych ADHD rzadko wygląda jak bieganie po klasie. Częściej
                jest to:
              </p>

              <ul>
                <li>wewnętrzny niepokój i „nadmiar myśli”,</li>
                <li>problemy z planowaniem i domykaniem zadań,</li>
                <li>
                  prokrastynacja połączona z nagłymi „zrywami” pod presją
                  deadlinu,
                </li>
                <li>impulsywne decyzje (finanse, praca, relacje),</li>
                <li>chaos w papierach, mailach, terminach.</li>
              </ul>

              <p>
                ADHD ma charakter przewlekły – nie pojawia się nagle po
                stresującym okresie i nie mija po miesiącu odpoczynku. Bywa natomiast,
                że latami pozostaje nierozpoznane.
              </p>

              <h2>ADHD a depresja</h2>

              <p>
                Zarówno ADHD, jak i depresja mogą dawać: spadek efektywności,
                kłopoty z organizacją, odkładanie zadań, zaniedbywanie
                obowiązków. Różne są jednak źródła tych trudności.
              </p>

              <p>
                W depresji na pierwszy plan wysuwają się objawy związane z
                nastrojem:
              </p>

              <ul>
                <li>przewlekły smutek, przygnębienie, poczucie pustki,</li>
                <li>
                  utrata zdolności cieszenia się (nawet tym, co wcześniej
                  sprawiało przyjemność),
                </li>
                <li>poczucie bezwartościowości, silne samokrytyczne myśli,</li>
                <li>
                  spowolnienie psychoruchowe – brak energii, „brak siły do
                  życia”.
                </li>
              </ul>

              <p>
                W ADHD nastrój może być zmienny, ale nie jest stale i głęboko
                obniżony. Osoba z ADHD potrafi doświadczać radości, entuzjazmu,
                silnego zaangażowania – szczególnie przy zadaniach, które ją
                interesują. Główny problem leży w utrzymaniu uwagi i
                zorganizowaniu działania.
              </p>

              <p>
                Często jednak te dwa światy się przenikają: lata życia z
                nierozpoznanym ADHD, poczucie „ciągłego zawalania” i bycia
                „gorszym od innych” mogą wtórnie prowadzić do epizodów depresji.
              </p>

              <h2>ADHD a zaburzenia lękowe</h2>

              <p>
                Lęk i ADHD również współwystępują bardzo często. W zaburzeniach
                lękowych typowe są:
              </p>

              <ul>
                <li>
                  uporczywe zamartwianie się („co jeśli…”, „na pewno coś
                  pójdzie nie tak”),
                </li>
                <li>objawy somatyczne: napięcie mięśni, kołatanie serca,</li>
                <li>
                  unikanie sytuacji, które budzą lęk (np. kontaktów społecznych,
                  wystąpień),
                </li>
                <li>
                  trudności z zasypianiem z powodu „gonitwy myśli” wokół
                  zagrożeń.
                </li>
              </ul>

              <p>
                W ADHD „gonitwa myśli” dotyczy często różnych pomysłów, bodźców,
                planów – niekoniecznie katastroficznych. Rozproszenie wynika
                bardziej z nadmiaru bodźców i trudności w ich filtrowaniu niż z
                ciągłego lękowego analizowania.
              </p>

              <p>
                Jednocześnie nieleczone ADHD może sprzyjać rozwojowi lęku –
                szczególnie społecznego (po wielu doświadczeniach porażek,
                wpadek, kompromitujących spóźnień).
              </p>

              <h2>ADHD a wypalenie zawodowe</h2>

              <p>
                Wypalenie zawodowe zwykle jest reakcją na długotrwały stres
                związany z pracą. Kluczowe objawy to:
              </p>

              <ul>
                <li>silne zmęczenie i wyczerpanie,</li>
                <li>cynizm, dystans wobec pracy,</li>
                <li>poczucie braku sensu i satysfakcji,</li>
                <li>spadek efektywności, rosnące zniechęcenie.</li>
              </ul>

              <p>
                Wypalenie jest zazwyczaj powiązane z konkretnym miejscem lub
                rolą (np. konkretną pracą). Zmiana środowiska, lepszy odpoczynek
                i redukcja stresu mogą wyraźnie poprawić sytuację.
              </p>

              <p>
                W ADHD problemy z organizacją, uwagą i impulsywnością są obecne
                niezależnie od tego, gdzie pracujesz. Oczywiście stres i
                przeciążenie mogą nasilać objawy, ale sam wzorzec
                dezorganizacji pojawia się również w innych obszarach życia –
                domu, relacjach, nauce.
              </p>

              <p>
                Co ciekawe, badania pokazują, że osoby z ADHD są bardziej
                narażone na wypalenie zawodowe – chroniczny chaos, działanie „od
                kryzysu do kryzysu” i stres z tym związany znacząco podnoszą to
                ryzyko.
              </p>

              <h2>Dlaczego różnicowanie jest takie ważne?</h2>

              <p>
                Jeżeli ktoś ma ADHD, a leczona jest u niego wyłącznie depresja
                lub lęk, część trudności może pozostać praktycznie
                niezmieniona. Z drugiej strony, osoba z depresją lub zaburzeniem
                lękowym, której przypisze się wyłącznie ADHD, może nie dostać
                potrzebnej pomocy.
              </p>

              <p>
                Dobra diagnoza uwzględnia:
              </p>

              <ul>
                <li>początek objawów – od kiedy trwają trudności,</li>
                <li>przebieg życia (szkoła, praca, relacje),</li>
                <li>obecność innych objawów (nastrój, lęk, sen, uzależnienia),</li>
                <li>historię rodzinną (np. ADHD, depresja, ChAD).</li>
              </ul>

              <p>
                Specjalista może korzystać z kwestionariuszy (np. DIVA-5),
                wywiadu klinicznego, czasem opinii bliskich osób, by „poukładać”
                całość obrazu.
              </p>

              <h2>Co możesz zrobić, jeśli nie wiesz, z czym masz do czynienia?</h2>

              <p>
                Jeśli czujesz, że samodzielne „rozkminianie” tego, czy to ADHD,
                depresja, lęk czy wypalenie zaczyna Cię przerastać – to już jest
                dobry powód, żeby porozmawiać ze specjalistą.
              </p>

              <ul>
                <li>
                  możesz zacząć od psychologa/psycholożki, który/a zrobi
                  wywiad, pomoże uporządkować objawy i zasugeruje dalsze kroki,
                </li>
                <li>
                  możesz zgłosić się też bezpośrednio do psychiatry – szczególnie
                  jeśli objawy są bardzo nasilone,
                </li>
                <li>
                  jeśli w Twojej okolicy trudno o specjalistę od ADHD – możesz
                  skorzystać z diagnozy online.
                </li>
              </ul>

              <p>
                Diagnoza nie jest „etykietką”, tylko narzędziem. Im lepiej
                rozumiemy, co się z nami dzieje, tym łatwiej dobrać skuteczną
                pomoc: terapię, strategię radzenia sobie, ewentualne leki.
              </p>

              <p>
                Jeśli masz poczucie, że od dawna „coś jest nie tak”, a kolejne
                próby samodzielnego ogarniania kończą się frustracją – naprawdę
                nie musisz robić tego sam_a. Szukanie wsparcia to krok w stronę
                ulgi, nie oznaka słabości.
              </p>
            </>
          ),
        };

      // 4. ARTYKUŁ: "ADHD w pracy, relacjach i nauce – jak sobie radzić?"
      case "adhd-w-pracy-relacjach-i-nauce-jak-sobie-radzic":
        return {
          title: "ADHD w pracy, relacjach i nauce – jak sobie radzić?",
          dateISO: "2025-11-10",
          dateLabel: "10 listopad 2025",
          subtitle:
            "Jak ADHD wpływa na karierę, związki i edukację – oraz jakie strategie mogą realnie pomóc?",
          content: (
            <>
              <p>
                Dorośli z ADHD często opisują swoje życie jako bieg z
                przeszkodami: zaległe zadania w pracy, bałagan w domu, napięte
                relacje z bliskimi, poczucie, że inni „po prostu lepiej ogarniają
                rzeczywistość”. ADHD to nie tylko „trudności z koncentracją” – to
                realny wpływ na różne role życiowe.
              </p>
              <p>
                W tym artykule przyglądamy się temu, jak ADHD oddziałuje na
                pracę, relacje i naukę oraz jakie praktyczne rozwiązania mogą
                ułatwić codzienne funkcjonowanie.
              </p>

              <h2>Krótko o ADHD u dorosłych</h2>

              <p>
                ADHD u dorosłych najczęściej oznacza:
              </p>

              <ul>
                <li>
                  problemy z organizacją i domykaniem spraw (zadania, projekty,
                  maile),
                </li>
                <li>
                  trudności z utrzymaniem koncentracji na nudnych lub
                  długotrwałych zadaniach,
                </li>
                <li>
                  impulsywność w decyzjach (np. finansowych, zawodowych,
                  relacyjnych),
                </li>
                <li>wewnętrzny niepokój, poczucie „ciągłego rozpędzenia”.</li>
              </ul>

              <p>
                Wszystko to przekłada się na konkretne sytuacje w pracy, domu,
                związku czy na studiach. ADHD nie definiuje jednak Twojej
                wartości ani inteligencji – bardzo wiele osób z ADHD jest
                kreatywnych, bystrych i pełnych energii. Potrzebują po prostu
                innych warunków i innych narzędzi.
              </p>

              <h2>ADHD w pracy – typowe wyzwania i rozwiązania</h2>

              <h3>Typowe wyzwania</h3>

              <ul>
                <li>
                  <strong>Skakanie po zadaniach:</strong> zaczynasz jedno,
                  odrywasz się, przechodzisz do kolejnego, wracasz do maili,
                  potem do czatu – i na końcu nic nie jest porządnie skończone.
                </li>
                <li>
                  <strong>Życie od deadlinu do deadlinu:</strong> projekty ruszają
                  dopiero w ostatniej chwili, kiedy presja czasu jest już bardzo
                  duża.
                </li>
                <li>
                  <strong>Trudność w dopasowaniu zawodu:</strong> częste zmiany
                  pracy lub kierunku studiów, bo początkowy entuzjazm szybko
                  opada.
                </li>
                <li>
                  <strong>Spóźnienia, chaotyczne CV, „dziury” w historii:</strong>{" "}
                  które trudniej wytłumaczyć na rozmowach rekrutacyjnych.
                </li>
              </ul>

              <h3>Możliwe rozwiązania</h3>

              <ul>
                <li>
                  <strong>Praca blokami:</strong> umawiaj się ze sobą, że np. 25
                  minut robisz tylko jedno zadanie (timer!), potem 5 minut
                  przerwy.
                </li>
                <li>
                  <strong>Jasne priorytety na dany dzień:</strong> 2–3 rzeczy,
                  które naprawdę musisz dziś ruszyć/ukończyć. Reszta jest „miło
                  by było, ale nie muszę”.
                </li>
                <li>
                  <strong>Rozbijanie dużych projektów na kroki:</strong> zamiast
                  „napisać raport”, wpisz w listę konkretne etapy.
                </li>
                <li>
                  <strong>Upraszczanie środowiska pracy:</strong> mniej otwartych
                  kart w przeglądarce, wyciszone powiadomienia, czystsze
                  biurko.
                </li>
                <li>
                  <strong>Dobór zawodu pod ADHD:</strong> wiele osób z ADHD
                  lepiej funkcjonuje w środowiskach dynamicznych, kreatywnych,
                  z większą autonomią niż w sztywnej strukturze korporacyjnej.
                </li>
              </ul>

              <h2>ADHD w relacjach – co się dzieje „pomiędzy ludźmi”?</h2>

              <h3>Jak ADHD komplikuje relacje?</h3>

              <ul>
                <li>
                  <strong>Zapominalstwo:</strong> niewysłane wiadomości,
                  nieoddzwonione telefony, pominięte daty i rocznice.
                </li>
                <li>
                  <strong>Impulsywne reakcje:</strong> szybkie, ostre komentarze
                  w emocjach; partner/partnerka może czuć się zraniony_a.
                </li>
                <li>
                  <strong>Trudność z obecnością „tu i teraz”:</strong> myśli
                  uciekają, mimo że druga osoba mówi o czymś ważnym.
                </li>
                <li>
                  <strong>Zmiany nastroju:</strong> wahania energii, entuzjazm
                  przeplatany zniechęceniem i frustracją.
                </li>
              </ul>

              <h3>Co może pomóc?</h3>

              <ul>
                <li>
                  <strong>Psychoedukacja w związku:</strong> kiedy partner/ka
                  rozumie, czym jest ADHD, łatwiej interpretować zachowania jako
                  objawy, a nie złą wolę.
                </li>
                <li>
                  <strong>„Zewnętrzne wsparcia pamięci”:</strong> wspólny
                  kalendarz, listy zakupów, aplikacje do dzielenia obowiązków.
                </li>
                <li>
                  <strong>Umówione sposoby komunikacji:</strong> np. sygnał
                  „to jest ważne, proszę skup się na chwilę”, albo regularne
                  „check-iny” raz w tygodniu.
                </li>
                <li>
                  <strong>Praca nad regulacją emocji:</strong> w terapii
                  można nauczyć się, jak rozpoznawać narastające napięcie i
                  zatrzymać się przed impulsywną reakcją.
                </li>
              </ul>

              <h2>ADHD w nauce – szkoła, studia, kursy</h2>

              <p>
                ADHD nie kończy się wraz ze szkołą. Na studiach i w edukacji
                dorosłych pojawiają się konkretne problemy:
              </p>

              <ul>
                <li>
                  trudność z długotrwałym uczeniem się „z podręcznika”,
                </li>
                <li>
                  odkładanie nauki do egzaminu na ostatnią chwilę („sesja na
                  dopalaczach”),
                </li>
                <li>
                  problemy z oddawaniem prac w terminie, choć materiał jest
                  opanowany,
                </li>
                <li>
                  szybkie zniechęcanie się kierunkiem, kiedy minie etap
                  „nowości”.
                </li>
              </ul>

              <h3>Strategie dla osoby uczącej się z ADHD</h3>

              <ul>
                <li>
                  <strong>Uczenie się krótkimi seriami:</strong> np. 20 minut
                  nauki, 10 minut przerwy, zamiast 3 godzin „na raz”.
                </li>
                <li>
                  <strong>Łączenie nauki z ruchem:</strong> chodzenie po pokoju,
                  ściskanie piłeczki, nauka na głos – wiele osób z ADHD lepiej
                  przyswaja materiał, gdy ciało też jest zaangażowane.
                </li>
                <li>
                  <strong>Materiał w formie atrakcyjnej:</strong> podcasty,
                  wideo, mind mapy zamiast samego „kucia tekstu”.
                </li>
                <li>
                  <strong>Planowanie „mikro-deadlinów”:</strong> zamiast jednego
                  dużego terminu – kilka mniejszych punktów kontrolnych.
                </li>
              </ul>

              <h2>Mocne strony ADHD</h2>

              <p>
                ADHD to nie tylko przeszkody. W odpowiednich warunkach i przy
                właściwym wsparciu wiele cech związanych z ADHD może stać się
                atutem:
              </p>

              <ul>
                <li>
                  <strong>Kreatywność i nieszablonowe myślenie:</strong> łatwość
                  generowania pomysłów, łączenia faktów z różnych dziedzin.
                </li>
                <li>
                  <strong>Szybkie tempo przetwarzania bodźców:</strong> w
                  dynamicznych sytuacjach możesz reagować szybciej niż inni.
                </li>
                <li>
                  <strong>Hiperfokus:</strong> kiedy coś naprawdę Cię wciągnie,
                  potrafisz skupić się bardzo intensywnie przez długi czas.
                </li>
                <li>
                  <strong>Duża energia:</strong> jeśli dobrze nią zarządzasz,
                  może napędzać projekty, zespoły, kreatywne działania.
                </li>
              </ul>

              <p>
                Kluczowe jest to, by nauczyć się minimalizować wpływ trudności
                (dezorganizacji, prokrastynacji, impulsywności) i jednocześnie
                wykorzystywać swoje naturalne zasoby.
              </p>

              <h2>Gdzie szukać wsparcia?</h2>

              <p>
                Jeśli masz wrażenie, że ADHD utrudnia Ci życie w pracy,
                relacjach czy edukacji, nie musisz zostawać z tym sam_a.
                Możliwości jest sporo:
              </p>

              <ul>
                <li>
                  konsultacja u psychologa/psychiatry z doświadczeniem w ADHD,
                </li>
                <li>
                  terapia ukierunkowana na funkcje wykonawcze i regulację
                  emocji,
                </li>
                <li>coaching ADHD, trening organizacji i planowania,</li>
                <li>
                  grupy wsparcia dla dorosłych z ADHD – także online,
                </li>
                <li>
                  w razie potrzeby – farmakoterapia dobrana przez psychiatrę.
                </li>
              </ul>

              <p>
                Wiele osób po diagnozie ADHD mówi, że największą ulgą było
                zrozumienie: „to nie moja wina, że tak działam – po prostu mój
                mózg działa inaczej”. To z kolei otwiera drogę do tego, by
                przestać walczyć ze sobą, a zacząć budować życie zaprojektowane
                pod własną neuroatypowość.
              </p>

              <p>
                Jeśli czujesz, że opis z tego tekstu pasuje do Twojej
                codzienności – to może być dobry moment, żeby zrobić kolejny
                krok i sięgnąć po pomoc.
              </p>
            </>
          ),
        };

              // 5. ARTYKUŁ: "Spektrum autyzmu u dorosłych – jak wygląda w praktyce?"
      case "spektrum-autyzmu-u-doroslych":
        return {
          title: "Spektrum autyzmu u dorosłych – jak wygląda w praktyce?",
          dateISO: "2025-10-29",
          dateLabel: "29 październik 2025",
          subtitle:
            "Autyzm u dorosłych często wygląda inaczej, niż w podręcznikowych opisach z dzieciństwa.",
          content: (
            <>
              <p>
                Spektrum autyzmu wielu osobom wciąż kojarzy się głównie z
                dziećmi – z trudnościami w mówieniu, brakiem kontaktu wzrokowego
                czy bardzo widocznymi stereotypiami ruchowymi. Tymczasem coraz
                więcej dorosłych odkrywa, że ich wieloletnie kłopoty z relacjami,
                przeciążeniem bodźcami czy funkcjonowaniem w pracy mogą wynikać
                właśnie z neurorozwojowego autyzmu.
              </p>

              <p>
                U dorosłych obraz autyzmu bywa subtelniejszy i bardziej
                zróżnicowany. Część osób nauczyła się latami „maskować”
                swoje trudności – dopasowywać zachowanie do oczekiwań otoczenia
                kosztem ogromnego wysiłku wewnętrznego. Na zewnątrz wszystko może
                wyglądać „w miarę OK”, podczas gdy w środku panuje stałe
                napięcie, zmęczenie i poczucie inności.
              </p>

              <h2>Jak może wyglądać spektrum autyzmu u dorosłych?</h2>

              <p>
                Objawy spektrum autyzmu u dorosłych są bardzo zróżnicowane, ale
                często pojawiają się w kilku obszarach:
              </p>

              <ul>
                <li>
                  <strong>Relacje społeczne:</strong> trudność w „czytaniu
                  między wierszami”, dosłowne rozumienie komunikatów,
                  niepewność co do zasad small talku, męczące spotkania w
                  większej grupie.
                </li>
                <li>
                  <strong>Komunikacja:</strong> wrażenie, że inni „wiedzą
                  intuicyjnie”, jak się zachować; zastanawianie się po spotkaniu,
                  czy coś nie zabrzmiało dziwnie; czasem też bardzo bezpośredni,
                  szczery sposób mówienia.
                </li>
                <li>
                  <strong>Nadwrażliwość / niedowrażliwość sensoryczna:</strong>{" "}
                  silne reagowanie na hałas, zapachy, światło, faktury ubrań,
                  dotyk; albo przeciwnie – szukanie intensywnych bodźców.
                </li>
                <li>
                  <strong>Potrzeba rutyny i przewidywalności:</strong> nagłe
                  zmiany planów wywołują silne napięcie; ustalone rytuały dnia dają
                  poczucie bezpieczeństwa.
                </li>
                <li>
                  <strong>Zainteresowania:</strong> bardzo intensywne, często
                  wąskie pasje – tematy, o których można mówić godzinami, dające
                  poczucie sensu i kompetencji.
                </li>
              </ul>

              <p>
                Wiele osób ze spektrum to tzw. „wysoko funkcjonujące” jednostki –
                kończą studia, pracują, tworzą rodziny. To, czego nie widać z
                zewnątrz, to koszt, jaki ponoszą: przeciążenie społeczne,
                chroniczne zmęczenie, częste epizody „załamania” po intensywnym
                dniu, trudności z regeneracją.
              </p>

              <h2>Maskowanie i różnice płciowe</h2>

              <p>
                Szczególnie osoby socjalizowane do roli kobiecej często uczą się
                maskować objawy: uśmiechać się, utrzymywać kontakt wzrokowy,
                naśladować zachowania innych, przygotowywać „gotowe skrypty”
                rozmów. Dzięki temu mogą latami „mieścić się” w oczekiwaniach
                społecznych – ale kosztem własnego dobrostanu.
              </p>

              <p>
                Maskowanie sprawia, że autyzm u dorosłych – zwłaszcza u kobiet
                – bywa przeoczany lub mylony z innymi trudnościami (np. lękiem
                społecznym, zaburzeniami osobowości, depresją). Diagnoza nierzadko
                pojawia się dopiero wtedy, gdy osoba przestaje mieć siły dalej
                „udawać, że wszystko jest normalne”.
              </p>

              <h2>Autyzm w pracy i relacjach</h2>

              <p>
                W pracy dorosła osoba ze spektrum autyzmu może:
              </p>

              <ul>
                <li>
                  świetnie sprawdzać się w zadaniach wymagających precyzji,
                  analizy, pracy z danymi lub jasno określonych procedur,
                </li>
                <li>
                  mieć trudności w nieformalnej części – rozmowy w kuchni,
                  „polityka” w firmie, chaotyczne spotkania bez agendy,
                </li>
                <li>
                  szybko się przeciążać w open space&apos;ach, na głośnych
                  wydarzeniach czy w pracy wymagającej ciągłego kontaktu z
                  ludźmi,
                </li>
                <li>
                  doświadczać niezrozumienia ze strony przełożonych („przecież
                  dajesz radę, o co chodzi?”).
                </li>
              </ul>

              <p>
                W relacjach prywatnych wyzwaniem może być rozmijanie się oczekiwań:
                druga osoba liczy na spontaniczność, „lekkie” rozmowy, częste
                spotkania w grupie, podczas gdy osoba ze spektrum potrzebuje
                raczej spokoju, jasnych zasad i czasu na samotną regenerację.
              </p>

              <h2>Co daje diagnoza spektrum autyzmu u dorosłych?</h2>

              <p>
                Diagnoza nie zmienia Twojej historii, ale może zmienić sposób,
                w jaki ją rozumiesz. Dla wielu osób jest momentem ulgi: wreszcie
                pojawia się spójne wyjaśnienie dotychczasowych doświadczeń.
              </p>

              <ul>
                <li>
                  łatwiej dobrać pracę i środowisko do własnych potrzeb
                  sensorycznych i społecznych,
                </li>
                <li>
                  można świadomie ustalać zasady w relacjach (np. jak często się
                  spotykamy, jak komunikujemy zmęczenie),
                </li>
                <li>
                  można nauczyć się strategii radzenia sobie z przeciążeniem i
                  planowania przerw,
                </li>
                <li>
                  łatwiej przestać obwiniać się za „inność” i zacząć ją traktować
                  jako element własnej tożsamości.
                </li>
              </ul>

              <h2>Kiedy warto zgłosić się na diagnozę?</h2>

              <p>Warto rozważyć konsultację, jeśli:</p>

              <ul>
                <li>
                  od lat czujesz się „inna/inny” w relacjach, ale nie wiesz,
                  dlaczego,
                </li>
                <li>
                  spotkania społeczne są bardzo męczące, długo dochodzisz do
                  siebie po intensywnych wydarzeniach,
                </li>
                <li>
                  doświadczasz silnej nadwrażliwości na dźwięki, światło, dotyk,
                </li>
                <li>
                  masz wrażenie, że funkcjonujesz głównie dzięki „maskowaniu”
                  i odgrywaniu roli,
                </li>
                <li>
                  pojawiają się wtórne trudności – wypalenie, lęk, depresja,
                  poczucie wyczerpania.
                </li>
              </ul>

              <p>
                Rozpoznanie spektrum autyzmu u dorosłego nie jest etykietą, ale
                narzędziem – pomaga dopasować środowisko, oczekiwania i styl
                życia do realnych potrzeb, zamiast na siłę wtłaczać się w
                schemat „normalności”.
              </p>
            </>
          ),
        };

      // 6. ARTYKUŁ: "Lęk, który nie odpuszcza – kiedy zgłosić się po pomoc?"
      case "lek-ktory-nie-odpuszcza":
        return {
          title: "Lęk, który nie odpuszcza – kiedy zgłosić się po pomoc?",
          dateISO: "2025-10-15",
          dateLabel: "15 październik 2025",
          subtitle:
            "Gdzie kończy się „zwykły stres”, a zaczynają zaburzenia lękowe wymagające wsparcia specjalisty?",
          content: (
            <>
              <p>
                Lęk jest naturalną reakcją organizmu – pomaga nam przetrwać,
                mobilizuje do działania, ostrzega przed zagrożeniem. Problem
                zaczyna się wtedy, gdy lęk pojawia się zbyt często, jest zbyt
                silny albo trwa zbyt długo – nawet wtedy, gdy obiektywnego
                zagrożenia nie ma.
              </p>

              <p>
                Dla wielu osób przewlekły lęk staje się codziennością: napięcie
                w ciele, kołatanie serca, trudności ze snem, nieustanne
                zamartwianie się. Pojawia się pytanie – czy to jeszcze stres,
                czy już zaburzenie lękowe, z którym warto zgłosić się po pomoc?
              </p>

              <h2>„Normalny” stres a zaburzenia lękowe</h2>

              <p>
                Krótkotrwały stres towarzyszy ważnym wydarzeniom – egzaminom,
                rozmowie o pracę, trudnym rozmowom. Gdy sytuacja mija, ciało i
                psychika wracają stopniowo do równowagi.
              </p>

              <p>
                W zaburzeniach lękowych lęk:
              </p>

              <ul>
                <li>pojawia się często, również bez wyraźnej przyczyny,</li>
                <li>
                  jest trudny do opanowania – „rozkręca się” mimo prób
                  uspokajania się,
                </li>
                <li>
                  prowadzi do unikania sytuacji (wyjść, spotkań, obowiązków),
                </li>
                <li>
                  znacząco utrudnia funkcjonowanie – w pracy, relacjach, codziennych
                  czynnościach.
                </li>
              </ul>

              <p>
                W praktyce wiele osób mówi: „ciągle jestem spięta_y”, „budzę się
                już z poczuciem niepokoju”, „nawet kiedy wszystko jest w porządku,
                moje ciało zachowuje się tak, jakby zaraz miało stać się coś
                złego”.
              </p>

              <h2>Jak może wyglądać przewlekły lęk?</h2>

              <p>
                Zaburzenia lękowe przyjmują różne formy. Do najczęstszych należą:
              </p>

              <ul>
                <li>
                  <strong>Lęk uogólniony (GAD):</strong> permanentne
                  zamartwianie się wieloma sprawami naraz („co jeśli coś się
                  stanie rodzinie?”, „co jeśli stracę pracę?”, „co jeśli popełnię
                  błąd?”), trudność z „wyłączeniem” myśli.
                </li>
                <li>
                  <strong>Fobia społeczna:</strong> silny lęk przed oceną,
                  kompromitacją, zrobieniem „czegoś głupiego” przy innych;
                  unikanie wystąpień, spotkań, czasem nawet prostych sytuacji jak
                  telefon do urzędu.
                </li>
                <li>
                  <strong>Napady paniki:</strong> nagłe epizody bardzo silnego
                  lęku połączone z objawami z ciała (kołatanie serca, duszność,
                  zawroty głowy, uczucie „zaraz umrę” albo „zwariuję”).
                </li>
                <li>
                  <strong>Ruminacje i natrętne myśli:</strong> powracające
                  scenariusze katastroficzne, analizowanie w myślach sytuacji
                  sprzed kilku dni czy lat, ciągłe „przeżuwanie” tych samych
                  tematów.
                </li>
              </ul>

              <h2>Sygnal, że lęk wymaga profesjonalnej pomocy</h2>

              <p>
                Warto rozważyć konsultację, jeśli zauważasz u siebie kilka z
                poniższych sytuacji:
              </p>

              <ul>
                <li>
                  lęk trwa tygodniami lub miesiącami i nie znika mimo tego, że
                  obiektywne sytuacje stresowe się skończyły,
                </li>
                <li>
                  zaczynasz unikać ważnych aktywności (spotkań, pracy, szkoły,
                  załatwiania spraw) głównie z powodu lęku,
                </li>
                <li>
                  masz trudności ze snem – długo nie możesz zasnąć, budzisz się
                  z silnym napięciem, budzisz się w nocy z kołataniem serca,
                </li>
                <li>
                  lęk zaczyna wpływać na relacje: wycofujesz się z kontaktu,
                  odwołujesz spotkania, częściej się irytujesz,
                </li>
                <li>
                  coraz częściej myślisz o sobie bardzo krytycznie („jestem
                  słaby_a”, „wszyscy inni dają radę”), czujesz narastającą
                  bezradność.
                </li>
              </ul>

              <p>
                Jeżeli oprócz lęku pojawiają się myśli o zrobieniu sobie krzywdy
                albo o tym, że „nie widzisz sensu dalszego życia”, to sygnał,
                by jak najszybciej skontaktować się z lekarzem, psychologiem
                klinicznym lub skorzystać z całodobowych telefonów zaufania.
              </p>

              <h2>Co może pomóc przy przewlekłym lęku?</h2>

              <p>
                Dobra wiadomość jest taka, że zaburzenia lękowe są jednymi z
                najlepiej przebadanych i najskuteczniej leczonych problemów
                psychicznych. Pomóc mogą m.in.:
              </p>

              <ul>
                <li>
                  <strong>Psychoterapia, szczególnie podejścia poznawczo-behawioralne:</strong>{" "}
                  praca nad sposobem myślenia, przekonaniami, reagowaniem na
                  objawy z ciała, stopniowe oswajanie sytuacji lękowych.
                </li>
                <li>
                  <strong>Farmakoterapia:</strong> leki przeciwlękowe i
                  przeciwdepresyjne dobierane przez psychiatrę, szczególnie przy
                  dużym nasileniu objawów.
                </li>
                <li>
                  <strong>Praca z ciałem i oddechem:</strong> techniki
                  relaksacyjne, ćwiczenia oddechowe, łagodne formy ruchu (np.
                  spacery, joga), dbanie o regularny sen.
                </li>
                <li>
                  <strong>Porządkowanie codzienności:</strong> stały rytm dnia,
                  przerwy w pracy, ograniczanie kofeiny i alkoholu, które mogą
                  nasilać napięcie.
                </li>
              </ul>

              <h2>Dlaczego nie warto czekać „aż samo przejdzie”?</h2>

              <p>
                Przewlekły lęk rzadko mija całkowicie bez zmiany stylu życia lub
                bez profesjonalnej pomocy. Dużo częściej stopniowo się nasila:
                z początku dotyczy tylko jednej sytuacji, potem coraz większej
                części życia.
              </p>

              <p>
                Im wcześniej zgłosisz się po wsparcie, tym szybciej można przerwać
                ten mechanizm. Dla wielu osób już kilka spotkań z psychologiem,
                sama psychoedukacja i proste strategie regulacji napięcia dają
                odczuwalną ulgę.
              </p>

              <p>
                Jeśli masz poczucie, że lęk „nie odpuszcza”, wraca mimo zmian
                w życiu i coraz bardziej ogranicza Twoją codzienność – to dobry
                moment, żeby poszukać profesjonalnej pomocy. Nie dlatego, że
                „sobie nie radzisz”, ale dlatego, że nie musisz robić tego
                wszystkiego sam_a.
              </p>
            </>
          ),
        };

      // Fallback – jeśli slug nie pasuje do żadnego artykułu
      default:
        return {
          title: "Artykuł",
          dateISO: "2025-01-01",
          dateLabel: "1 stycznia 2025",
          subtitle: "",
          content: (
            <>
              <p>
                ADHD u dorosłych rzadko wygląda tak, jak stereotypowe
                „nadpobudliwe dziecko w szkole”. U wielu osób dominuje raczej
                wewnętrzny chaos, problemy z organizacją, ciągłe odkładanie
                spraw na później i poczucie, że inni „jakoś lepiej ogarniają
                życie”.
              </p>
              <p>
                Ten tekst jest wersją domyślną. Dodaj kolejne artykuły, tak jak
                w przykładzie powyżej, dopasowując tytuł, datę i treść.
              </p>
            </>
          ),
        };
    }
  })();

  return (
    <>
      <Header />

      <div className="offers" style={{ marginTop: "5rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 mt-3">
              <div className="member d-flex flex-column align-items-start h-100">
                <div className="member-info" style={{ width: "100%" }}>
                  <p
                    className="blog-article-back"
                    style={{ marginBottom: "2rem" }}
                  >
                    <Link to="/blog">&larr; Wróć do listy artykułów</Link>
                  </p>

                  <h1
                    className="blog-article-title"
                    style={{
                      fontSize: "2rem",
                      marginBottom: "0.3rem",
                      textAlign: "left",
                    }}
                  >
                    {article.title}
                  </h1>

                  {article.subtitle && (
                    <p className="blog-article-subtitle">
                      {article.subtitle}
                    </p>
                  )}

                  <p className="blog-article-meta">
                    Opublikowano:{" "}
                    <time dateTime={article.dateISO}>
                      {article.dateLabel}
                    </time>
                  </p>

                  <div className="blog-article-content">{article.content}</div>

                  <div className="text-end" style={{ marginTop: "2rem" }}>
                    <Link to="/umow-wizyte" className="more-btn">
                      <BsFillArrowRightCircleFill /> Umów się na konsultację
                      <i className="bx bx-chevron-right"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Article;
